import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { CommentPlan, commentPlanLabels, commentPlans } from '@constants'
import { useCommentMutations } from '@scene'
import { Box, Typography } from '@mui/material'
import ElementListSearchBar from '@editor/components/ElementListPane/components/ElementListSearchBar'
import { storeyToLabel } from '@editor/components/ElementListPane/utils/storeys'
import { useGuidToElementIncludingRips } from '@editor/hooks'
import { useCommentsStore, useModelStore } from '@editorStores'
import { usePlanElementSelection, useSelectedPlanElementId } from '@editorHooks'
import CommentListFilterButton from './CommentListFilterButton'
import CommentRow from './CommentRow'
import {
  buildDuplicateCommentDTO,
  collectCommentStoreys,
  commentMatchesPlans,
  commentMatchesSearch,
  commentMatchesStoreys,
} from './utils'

/**
 * Project-wide comments as a list pane — shared across the StructuralPlanning / GlobalLoads element
 * list and Results2. Lists every comment with its text (inline-editable), the plans + storeys it is
 * shown on, and per-row copy/delete actions, plus a search box. Selecting a row selects the comment
 * in the 3D scene (the scene's CommentsController reads `selectedPlanElement`).
 */
const CommentsListPane = (): ReactElement => {
  const comments = useCommentsStore(state => state.comments)
  const selectedId = useSelectedPlanElementId('comment')
  const { selectPlanElement } = usePlanElementSelection()
  const setShowComments = useModelStore(state => state.setShowComments)

  const guidToElement = useGuidToElementIncludingRips()

  const { createComment, updateCommentFields, deleteComment } = useCommentMutations()

  // Surface comments in the 3D scene while the tab is open (scene rendering is gated by
  // `showComments`, independent of the removed comments mode).
  useEffect(() => {
    setShowComments(true)
  }, [setShowComments])

  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState<CommentPlan[]>([])
  const [storeyFilter, setStoreyFilter] = useState<string[]>([])

  const availableStoreys = useMemo(() => collectCommentStoreys(comments), [comments])

  const planOptions = useMemo(
    () => commentPlans.map(plan => ({ value: plan, label: commentPlanLabels[plan] })),
    [],
  )
  const storeyOptions = useMemo(
    () => availableStoreys.map(storey => ({ value: storey, label: storeyToLabel(storey) })),
    [availableStoreys],
  )

  const filteredComments = useMemo(
    () =>
      comments.filter(
        comment =>
          commentMatchesSearch(comment, search) &&
          commentMatchesPlans(comment, planFilter) &&
          commentMatchesStoreys(comment, storeyFilter),
      ),
    [comments, search, planFilter, storeyFilter],
  )

  const handleSelect = useCallback(
    (id: string) => selectPlanElement('comment', id),
    [selectPlanElement],
  )

  const handleCommitText = useCallback(
    (id: string, text: string) => updateCommentFields(id, { text }),
    [updateCommentFields],
  )

  const handleDuplicate = useCallback(
    (comment: Comment3D) => createComment(buildDuplicateCommentDTO(comment)),
    [createComment],
  )

  const handleUnlink = useCallback(
    (id: string) => updateCommentFields(id, { element_guid: null }),
    [updateCommentFields],
  )

  return (
    <Box
      data-cy="comments-list-pane"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'common.white',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.75,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <CommentListFilterButton
          dataCy="comments-filter"
          sections={[
            {
              key: 'plan',
              title: 'Plan',
              options: planOptions,
              selected: planFilter,
              onChange: next => setPlanFilter(next as CommentPlan[]),
            },
            {
              key: 'storey',
              title: 'Geschoss',
              options: storeyOptions,
              selected: storeyFilter,
              onChange: setStoreyFilter,
            },
          ]}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ElementListSearchBar value={search} onChange={setSearch} />
        </Box>
      </Box>

      {filteredComments.length === 0 ? (
        <Box
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
        >
          <Typography variant="body2" color="text.secondary" data-cy="comments-list-empty">
            {comments.length === 0 ? 'Keine Kommentare' : 'Keine Treffer'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {filteredComments.map(comment => (
            <CommentRow
              key={comment.id}
              comment={comment}
              selected={selectedId === comment.id}
              linkedElementExists={!!comment.element_guid && !!guidToElement[comment.element_guid]}
              onSelect={handleSelect}
              onCommitText={handleCommitText}
              onDuplicate={handleDuplicate}
              onDelete={deleteComment}
              onUnlink={handleUnlink}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default CommentsListPane
